import React, { FC, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Box,
} from "@mui/material";
import { useDebounce } from "../../../hooks/useDebounce";

interface ClinicSearchProps {
  clinics: { name: string }[];
  queryClinic: { page: string; perPage: string; search: string };
  setSelectedClinic: React.Dispatch<React.SetStateAction<string>>;
  selectedClinic: string;
  setQueryClinic: React.Dispatch<
    React.SetStateAction<{ page: string; perPage: string; search: string }>
  >;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ClinicSearchForm: FC<ClinicSearchProps> = ({
  clinics,
  queryClinic,
  setQueryClinic,
  selectedClinic,
  setSelectedClinic,
  setOpen,
}) => {
  const [searchTerm, setSearchTerm] = useState(queryClinic.search);
  const [filteredClinics, setFilteredClinics] = useState(clinics);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (debouncedSearchTerm) {
      const matches = clinics.filter((clinic) =>
        clinic.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
      setFilteredClinics(matches);
      setQueryClinic((prevState) => ({
        ...prevState,
        search: debouncedSearchTerm,
      }));
    } else {
      setFilteredClinics(clinics);
      setQueryClinic((prevState) => ({ ...prevState, search: "" }));
    }
  }, [debouncedSearchTerm, clinics, setQueryClinic]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClinicSelect = (clinicName: string) => {
    setSelectedClinic(clinicName);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedClinic("");
    setQueryClinic((prevState) => ({ ...prevState, search: "" }));
  };

  const handleNextPageChange = () => {
    setQueryClinic((prevState) => ({
      ...prevState,
      page: (parseInt(prevState.page) + 1).toString(),
    }));
  };

  const handleBackPageChange = () => {
    setQueryClinic((prevState) => ({
      ...prevState,
      page: (parseInt(prevState.page) - 1).toString(),
    }));
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item xs={10} sm={8}>
        <Typography variant="h6" gutterBottom>
          Klinik suchen
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          label="Name der Klinik"
          value={searchTerm}
          onChange={handleSearchChange}
          helperText="Geben Sie den Namen der Klinik ein, um zu suchen"
        />
        <List
          sx={{
            maxHeight: "300px",
            overflowY: "auto",
            border: "1px solid #ccc",
            borderRadius: "4px",
            marginTop: "16px",
          }}
        >
          {filteredClinics.length > 0 ? (
            filteredClinics.map((clinic, index) => (
              <React.Fragment key={index}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleClinicSelect(clinic.name)}
                    selected={clinic.name === selectedClinic}
                  >
                    <ListItemText primary={clinic.name} />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="Keine Übereinstimmungen gefunden" />
            </ListItem>
          )}
        </List>
        {selectedClinic && (
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ marginTop: "16px" }}
          >
            Ausgewählte Klinik: {selectedClinic}
          </Typography>
        )}
        <Box
          width="100%"
          display="center"
          justifyContent="center"
          alignContent="center"
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearSearch}
            disabled={!searchTerm && !selectedClinic}
            sx={{ marginTop: "16px", mr: 2 }}
          >
            Suche löschen
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseModal}
            disabled={!searchTerm && !selectedClinic}
            sx={{ marginTop: "16px" }}
          >
            Akzeptieren
          </Button>
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ marginTop: "16px" }}
          onClick={handleNextPageChange}
        >
          nächste Seite
        </Button>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={parseInt(queryClinic.page) === 1}
          sx={{ marginTop: "16px" }}
          onClick={handleBackPageChange}
        >
          vorherige Seite
        </Button>
      </Grid>
    </Grid>
  );
};
